import React from 'react';
import {HighLevelNumbersBox, HighLevelNumbersContainer} from './HighLevelNumbers.styled';
import {createFragmentContainer, graphql} from 'react-relay';
import {HighLevelNumber} from './HighLevelNumber';
import {NUMBER_TYPE} from './util/BudgetUtils';

const BillableAndNonBillableHoursNumbers = ({
	billableActualMinutes,
	nonBillableActualMinutes,
	percentageActualBillableTime,
	projectedBillableMinutes,
	projectedNonBillableMinutes,
	percentageProjectedBillableTime,
}) => (
	<>
		<HighLevelNumbersBox wide>
			<HighLevelNumber
				labelTranslationId={'common.actual_billable_hours'}
				value={billableActualMinutes}
				numberType={NUMBER_TYPE.TIME}
			/>
			<HighLevelNumber
				labelTranslationId={'common.actual_non_billable_hours'}
				value={nonBillableActualMinutes}
				numberType={NUMBER_TYPE.TIME}
			/>
			<HighLevelNumber
				labelTranslationId={'common.percentage_actual_billable_hours'}
				value={percentageActualBillableTime}
				numberType={NUMBER_TYPE.PERCENTAGE}
			/>
		</HighLevelNumbersBox>
		<HighLevelNumbersBox wide>
			<HighLevelNumber
				labelTranslationId={'common.projected_total_billable_hours'}
				value={projectedBillableMinutes}
				numberType={NUMBER_TYPE.TIME}
			/>
			<HighLevelNumber
				labelTranslationId={'common.projected_total_non_billable_hours'}
				value={projectedNonBillableMinutes}
				numberType={NUMBER_TYPE.TIME}
			/>
			<HighLevelNumber
				labelTranslationId={'common.percentage_projected_billable_hours'}
				value={percentageProjectedBillableTime}
				numberType={NUMBER_TYPE.PERCENTAGE}
			/>
		</HighLevelNumbersBox>
	</>
);

const BaselineNumbers = ({baselineMinutes, baselineMinusProjectedBillableMinutes}) => (
	<HighLevelNumbersBox wide>
		<HighLevelNumber labelTranslationId={'baseline.hours'} value={baselineMinutes} numberType={NUMBER_TYPE.TIME} />
		<HighLevelNumber
			labelTranslationId={'common.baseline_minus_projected_total_billable_hours'}
			value={baselineMinusProjectedBillableMinutes}
			numberType={NUMBER_TYPE.TIME}
		/>
	</HighLevelNumbersBox>
);

const HighLevelTimeNumbers = ({project}) => {
	const {financialNumbers, useBaseline, billable} = project;

	const {
		registeredMinutes,
		billableActualMinutes,
		nonBillableActualMinutes,
		baselineMinutes,
		scopeApprovedMinutes,
		projectedTotalMinutes,
		projectedBillableMinutes,
		projectedNonBillableMinutes,
	} = financialNumbers;

	const percentageActualBillableTime = registeredMinutes > 0 ? billableActualMinutes / registeredMinutes : 0;
	const percentageProjectedBillableTime = projectedTotalMinutes > 0 ? projectedBillableMinutes / projectedTotalMinutes : 0;

	const baselineMinusProjectedBillableMinutes = baselineMinutes - projectedBillableMinutes;
	const baselineMinusProjectedMinutes = baselineMinutes - projectedTotalMinutes;
	const estimatedMinusProjectedMinutes = scopeApprovedMinutes - projectedTotalMinutes;

	const isBillableProject = !!billable;
	return (
		<HighLevelNumbersContainer data-cy={'summary-table'}>
			{isBillableProject && useBaseline && projectedNonBillableMinutes > 0 && (
				<>
					<BillableAndNonBillableHoursNumbers
						billableActualMinutes={billableActualMinutes}
						nonBillableActualMinutes={nonBillableActualMinutes}
						percentageActualBillableTime={percentageActualBillableTime}
						projectedBillableMinutes={projectedBillableMinutes}
						projectedNonBillableMinutes={projectedNonBillableMinutes}
						percentageProjectedBillableTime={percentageProjectedBillableTime}
					/>
					<BaselineNumbers
						baselineMinutes={baselineMinutes}
						baselineMinusProjectedBillableMinutes={baselineMinusProjectedBillableMinutes}
					/>
				</>
			)}
			{isBillableProject && useBaseline && projectedNonBillableMinutes === 0 && (
				<HighLevelNumbersBox wide>
					<HighLevelNumber
						labelTranslationId={'common.actual_hours'}
						value={registeredMinutes}
						numberType={NUMBER_TYPE.TIME}
					/>
					<HighLevelNumber
						labelTranslationId={'common.projected_total_hours'}
						value={projectedTotalMinutes}
						numberType={NUMBER_TYPE.TIME}
					/>
					<HighLevelNumber
						labelTranslationId={'baseline.hours'}
						value={baselineMinutes}
						numberType={NUMBER_TYPE.TIME}
					/>
					<HighLevelNumber
						labelTranslationId={'common.baseline_minus_projected_total_hours'}
						value={baselineMinusProjectedMinutes}
						numberType={NUMBER_TYPE.TIME}
					/>
				</HighLevelNumbersBox>
			)}
			{isBillableProject && !useBaseline && projectedNonBillableMinutes > 0 && (
				<BillableAndNonBillableHoursNumbers
					billableActualMinutes={billableActualMinutes}
					nonBillableActualMinutes={nonBillableActualMinutes}
					percentageActualBillableTime={percentageActualBillableTime}
					projectedBillableMinutes={projectedBillableMinutes}
					projectedNonBillableMinutes={projectedNonBillableMinutes}
					percentageProjectedBillableTime={percentageProjectedBillableTime}
				/>
			)}
			{isBillableProject && !useBaseline && projectedNonBillableMinutes === 0 && (
				<HighLevelNumbersBox wide>
					<HighLevelNumber
						labelTranslationId={'common.actual_hours'}
						value={registeredMinutes}
						numberType={NUMBER_TYPE.TIME}
					/>
					<HighLevelNumber
						labelTranslationId={'common.projected_total_hours'}
						value={projectedTotalMinutes}
						numberType={NUMBER_TYPE.TIME}
					/>
					<HighLevelNumber
						labelTranslationId={'common.estimated_hours'}
						value={scopeApprovedMinutes}
						numberType={NUMBER_TYPE.TIME}
					/>
					<HighLevelNumber
						labelTranslationId={'common.estimated_minus_projected_total_hours'}
						value={estimatedMinusProjectedMinutes}
						numberType={NUMBER_TYPE.TIME}
					/>
				</HighLevelNumbersBox>
			)}
			{!isBillableProject && useBaseline && (
				<HighLevelNumbersBox wide>
					<HighLevelNumber
						labelTranslationId={'common.actual_hours'}
						value={registeredMinutes}
						numberType={NUMBER_TYPE.TIME}
					/>
					<HighLevelNumber
						labelTranslationId={'common.projected_total_hours'}
						value={projectedTotalMinutes}
						numberType={NUMBER_TYPE.TIME}
					/>
					<HighLevelNumber
						labelTranslationId={'baseline.hours'}
						value={baselineMinutes}
						numberType={NUMBER_TYPE.TIME}
					/>
					<HighLevelNumber
						labelTranslationId={'common.baseline_minus_projected_total_hours'}
						value={baselineMinusProjectedMinutes}
						numberType={NUMBER_TYPE.TIME}
					/>
				</HighLevelNumbersBox>
			)}
			{!isBillableProject && !useBaseline && (
				<HighLevelNumbersBox wide>
					<HighLevelNumber
						labelTranslationId={'common.actual_hours'}
						value={registeredMinutes}
						numberType={NUMBER_TYPE.TIME}
					/>
					<HighLevelNumber
						labelTranslationId={'common.projected_total_hours'}
						value={projectedTotalMinutes}
						numberType={NUMBER_TYPE.TIME}
					/>
					<HighLevelNumber
						labelTranslationId={'common.estimated_hours'}
						value={scopeApprovedMinutes}
						numberType={NUMBER_TYPE.TIME}
					/>
					<HighLevelNumber
						labelTranslationId={'common.estimated_minus_projected_total_hours'}
						value={estimatedMinusProjectedMinutes}
						numberType={NUMBER_TYPE.TIME}
					/>
				</HighLevelNumbersBox>
			)}
		</HighLevelNumbersContainer>
	);
};

export default createFragmentContainer(HighLevelTimeNumbers, {
	company: graphql`
		fragment HighLevelTimeNumbers_company on Company {
			id
		}
	`,
	project: graphql`
		fragment HighLevelTimeNumbers_project on ProjectType {
			id
			useBaseline
			billable
			financialNumbers(convertToProjectCurrency: true) {
				registeredMinutes
				billableActualMinutes
				nonBillableActualMinutes
				forecastMinutes
				baselineMinutes
				scopeApprovedMinutes
				projectedTotalMinutes
				projectedBillableMinutes
				projectedNonBillableMinutes
			}
		}
	`,
});
